import React, { Fragment, useEffect, useMemo, useCallback, Suspense, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { CPStore } from 'apps/CPApp';
import { getStatusCardTitle } from 'components/Estimate/StatusCard/getStatusCardTitle';
import FaasWidget from 'components/Experiments/Faas/FaasWidget';
import OneToManyMerchantInfoCard from 'components/PaymentReq/OneToMany/MerchantInfoCard/OneToManyMerchantInfoCard';
import Card from 'components/Shared/Card/Card';
import ContactInfoActionCard from 'components/Shared/Contact/InfoAction/ContactInfoActionCard';
import { FeedbackLink } from 'components/Shared/Feedback/Feedback';
import Separator from 'components/Shared/Separator/Separator';
import SidePanel from 'components/Shared/SidePanel/SidePanel';
import { useAcceptedSuccessScreen } from 'hooks/useAcceptedSuccessScreen';
import { useComponentRenderer } from 'hooks/useComponentRenderer';
import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import WavefrontReporter from 'reporting/wavefront/WavefrontReporter';
import { shouldHandleCreditFloorDecline, isCreditFloorTrtB } from 'shared/creditFloorHelpers';
import { getPaymentErrorMessage } from 'shared/pagesHelpers';
import { getActiveSubscriptionPaymentErrorType } from 'shared/subscriptionHelpers';
import { isChangeOrderEnabled } from 'shared/utils';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import {
  downloadPDFDocument,
  fetchPDFDocument,
  saveReceiptAsPdf,
  showInvoiceViewer,
} from 'store/invoiceDocument/slice';
import {
  isBatchPaymentsEnabled,
  isPaymentRetryExperimentTreatmentA,
  isPaymentRetryExperimentTreatmentB,
  isInBillingAddressExperiment,
} from 'store/ixp/selector';
import { modalActions } from 'store/modal/slice';
import { paymentActions } from 'store/payment/slice';
import { saleSelectors } from 'store/sale/selectors';
import { RootState, AppDispatch } from 'store/store';
import { walletSelectors } from 'store/wallet/selectors';
import { breakpoints } from 'styles/cp';
import { CompanyInfo } from 'types/CompanyInfo';
import { Payment as PaymentType } from 'types/Payment';
import { TXN_MAP, SALE_MAP, PageVariant } from 'types/constants';

// Lazy load the deposit success screen component
const LazyDepositSuccessScreen = React.lazy(
  () => import('components/Core/SuccessScreen/Deposit/DepositSuccessScreen')
);

// Lazy load the generic success screen component for payment requests
const LazySuccessScreen = React.lazy(
  () => import('components/Core/SuccessScreen/SuccessScreen/SuccessScreen')
);

const SpinnerCentered = () => (
  <React.Fragment>
    <style jsx>{`
      .spinner-wrapper {
        width: 100px;
        margin: 50px auto;
      }
    `}</style>
    <div className="spinner-wrapper">
      <div>Loading...</div>
    </div>
  </React.Fragment>
);

const wavefrontReporter = WavefrontReporter.getInstance();
const splunkReporter = SplunkReporter.getInstance();
const logger = 'pages/estimate';

interface UnifiedPageProps extends RootState {
  dispatch: AppDispatch;
  pageVariant?: PageVariant;
}

const undefinedPage: React.FC<UnifiedPageProps> = (props) => {
  const intl = useIntl();
  const {
    dispatch,
    invoiceDocument,
    companyInfo,
    sale,
    insight,
    config,
    payment,
    featureFlags,
    wallet,
    auth,
    error,
    i18n,
    ixp,
    modal,
    postInvoiceSurvey,
    toast,
    chat,
  } = props;

  const shouldShowChangeOrderFeatures = useCallback(() => {
    return isChangeOrderEnabled(saleSelectors.typeSelector(sale), featureFlags);
  }, [sale, featureFlags]);

  const getDisplayAmount = useCallback(() => {
    const pageVariant = insightSelectors.pageVariantSelector(insight);
    const amount =
      pageVariant === PageVariant.DEPOSIT_ACCEPTED
        ? saleSelectors.depositAmountSelector(sale)
        : saleSelectors.amountSelector(sale);
    return amount || 0;
  }, [insight, sale]);

  // Get status card title
  const getStatusCardTitleCallback = useCallback(() => {
    return getStatusCardTitle({
      pageVariant: insightSelectors.pageVariantSelector(insight) as PageVariant,
      transactionType: saleSelectors.typeSelector(sale) || 'estimate',
      featureFlags,
      estimateStatus: saleSelectors.estimateStatus(sale),
    });
  }, [insight, sale, featureFlags]);

  // Compute batch payments enabled status outside callback to avoid props in dependency array
  const isBatchPaymentsEnabledValue = isBatchPaymentsEnabled(props);
  // Compute payment retry experiment treatment status
  const isPaymentRetryExperimentTreatmentAValue = isPaymentRetryExperimentTreatmentA(props);
  const isPaymentRetryExperimentTreatmentBValue = isPaymentRetryExperimentTreatmentB(props);

  // Track the last paymentSessionCounter we handled for auto-edit mode
  // This allows us to trigger edit mode on each new credit floor decline
  const lastHandledSessionCounterRef = useRef<number | null>(null);
  // Compute billing address experiment treatment status
  const isInBillingAddressExperimentValue = isInBillingAddressExperiment(props);

  // Track if we've already fired the payment retry error viewed event
  const paymentRetryErrorViewedRef = useRef(false);
  // Track if we've already fired the payment retry workflow error viewed event
  const paymentRetryWorkflowErrorViewedRef = useRef(false);
  // Track if we've already fired the billing info error viewed event
  const billingInfoErrorViewedRef = useRef(false);

  const renderErrorIfAny = useCallback(() => {
    // Check if payment object exists and is properly initialized
    if (!payment || typeof payment !== 'object') return null;

    const {
      paymentError,
      paymentStatus,
      paymentMethodType,
      payPalProcessor,
      isDateScheduled,
      isAutoPayOn,
      partialPaymentEnabled,
      creditFloorDeclineAmount,
    } = payment as PaymentType;

    const companyName = companyInfoSelectors.nameSelector(companyInfo);
    const region = companyInfoSelectors.regionSelector(companyInfo);
    const currency = saleSelectors.currencySelector(sale);
    const saleType = saleSelectors.typeSelector(sale);

    // Calculate subscription payment error type if this is a subscription
    const pageVariant = insightSelectors.pageVariantSelector(insight) as PageVariant;
    const activeSubscriptionPaymentErrorType =
      pageVariant === PageVariant.SUBSCRIPTION
        ? getActiveSubscriptionPaymentErrorType(sale, wallet, auth)
        : undefined;

    return getPaymentErrorMessage({
      error,
      paymentError,
      paymentStatus,
      companyName,
      featureFlags,
      logger,
      paymentMethodType,
      payPalProcessor,
      isDateScheduled,
      isAutoPayOn,
      region,
      activeSubscriptionPaymentErrorType,
      creditFloorDeclineAmount,
      currency,
      transactionType: saleType,
      isBatchPaymentsEnabled: isBatchPaymentsEnabledValue,
      onEditAmountClick: ({ skipTracking } = {}) => {
        // tracking was done elsewhere
        if (!skipTracking) {
          SegmentIO.editAmountSelected();
        }
        dispatch(paymentActions.setPaymentAmountEditMode(true));
      },
      partialPaymentEnabled,
      isPaymentRetryExperimentTreatmentA: isPaymentRetryExperimentTreatmentAValue,
      isPaymentRetryExperimentTreatmentB: isPaymentRetryExperimentTreatmentBValue,
      isInBillingAddressExperiment: isInBillingAddressExperimentValue,
      onPaymentRetryErrorViewed: () => {
        if (!paymentRetryErrorViewedRef.current) {
          paymentRetryErrorViewedRef.current = true;
          SegmentIO.paymentRetryErrorViewed();
        }
      },
      onPaymentRetryWorkflowErrorViewed: () => {
        if (!paymentRetryWorkflowErrorViewedRef.current) {
          paymentRetryWorkflowErrorViewedRef.current = true;
          SegmentIO.paymentRetryWorkflowErrorViewed();
        }
      },
      onBillingInfoErrorViewed: () => {
        if (!billingInfoErrorViewedRef.current) {
          billingInfoErrorViewedRef.current = true;
          SegmentIO.billingInfoErrorViewed();
        }
      },
    });
  }, [
    payment,
    companyInfo,
    error,
    featureFlags,
    insight,
    sale,
    wallet,
    auth,
    isBatchPaymentsEnabledValue,
    isInBillingAddressExperimentValue,
    isPaymentRetryExperimentTreatmentAValue,
    isPaymentRetryExperimentTreatmentBValue,
    dispatch,
  ]);

  // TreatmentB auto-edit trigger: automatically enter edit mode when credit floor decline occurs
  // Uses paymentSessionCounter to detect new failures and trigger edit mode on each one
  useEffect(() => {
    if (!payment || typeof payment !== 'object') return;

    const {
      paymentStatus,
      isDateScheduled,
      isAutoPayOn,
      partialPaymentEnabled,
      paymentSessionCounter,
    } = payment as PaymentType;

    // Skip if we've already handled this session counter
    if (lastHandledSessionCounterRef.current === paymentSessionCounter) return;

    const saleType = saleSelectors.typeSelector(sale);
    if (!saleType) return; // Guard against null sale type

    const isEditAmountAllowed = partialPaymentEnabled && !isDateScheduled && !isAutoPayOn;
    const isCreditFloorDecline = isCreditFloorTrtB({
      ixp,
      featureFlags,
      payment: payment as PaymentType,
    });

    if (
      shouldHandleCreditFloorDecline({
        paymentStatus: paymentStatus as any,
        transactionType: saleType as any,
        isBatchPaymentsEnabled: isBatchPaymentsEnabledValue as any,
        isEditAmountAllowed: isEditAmountAllowed as any,
        isCreditFloorDecline: isCreditFloorDecline as any,
      })
    ) {
      lastHandledSessionCounterRef.current = paymentSessionCounter;
      dispatch(paymentActions.setPaymentAmountEditMode(true));
    }
  }, [
    payment,
    sale,
    isBatchPaymentsEnabledValue,
    isPaymentRetryExperimentTreatmentBValue,
    dispatch,
    ixp,
    featureFlags,
  ]);

  // PDF handlers
  const viewPDF = useCallback(() => {
    dispatch(showInvoiceViewer());
  }, [dispatch]);

  const handleDownloadPDF = useCallback(() => {
    dispatch(downloadPDFDocument());
  }, [dispatch]);

  // Memoized PDF status
  const pdfStatus = useMemo(() => {
    const { fetchPDFStatus, PDFDocumentBlobUrl } = invoiceDocument || {};

    switch (fetchPDFStatus) {
      case TXN_MAP.STATUS.IN_PROGRESS:
        return { downloading: true, error: false, url: undefined };
      case TXN_MAP.STATUS.SUCCESS:
        return { downloading: false, error: false, url: PDFDocumentBlobUrl };
      case TXN_MAP.STATUS.ERROR:
        return { downloading: false, error: true, url: undefined };
      default:
        return { downloading: false, error: false, url: undefined };
    }
  }, [invoiceDocument]);

  // Component renderer hook
  const mainCard = useComponentRenderer({
    pageVariant: insightSelectors.pageVariantSelector(insight) as PageVariant,
    sale,
    ixp,
    insight,
    companyInfo,
    auth,
    shouldShowChangeOrderFeatures,
    getDisplayAmount,
    getStatusCardTitle: getStatusCardTitleCallback,
    pdfStatus,
    config,
    handleDownloadPDF,
    payment,
    featureFlags,
  });

  const acceptedSuccessScreen = useAcceptedSuccessScreen({
    isChangeOrder: shouldShowChangeOrderFeatures(),
    sale,
    companyInfo,
    invoiceDocument,
    viewPDF,
    handleDownloadPDF,
  });

  const pageVariant = insightSelectors.pageVariantSelector(insight) as PageVariant;
  const companyLogoUrl = companyInfoSelectors.logoSelector(companyInfo);
  const paymentMethodType = payment?.paymentMethodType;

  // Effects
  useEffect(() => {
    const token = insightSelectors.tokenSelector(insight);
    const transactionType = saleSelectors.typeSelector(sale);
    const { fetchPDFStatus } = invoiceDocument || {};

    if (
      transactionType !== TXN_MAP.TYPES.PAYMENT_REQUEST &&
      transactionType !== TXN_MAP.TYPES.SUBSCRIPTION &&
      fetchPDFStatus !== TXN_MAP.STATUS.SUCCESS
    ) {
      dispatch?.(fetchPDFDocument(token));
    }
  }, [dispatch, insight]);

  useEffect(() => {
    // Track payment success for estimate accepted (not for deposit or declined)
    if (pageVariant === PageVariant.ESTIMATE_ACCEPTED) {
      wavefrontReporter.trackPayment({
        paymentMethod: 'noPaymentMethodInEstimates',
        status: 'success',
        isPaymentUsingWallet: walletSelectors.isPaymentUsingWallet({
          wallet,
          payment,
        } as RootState),
      });
    } else if (pageVariant === PageVariant.ESTIMATE_DECLINED) {
      wavefrontReporter.trackPayment({
        paymentMethod: 'noPaymentMethodInEstimates',
        status: 'declined',
        isPaymentUsingWallet: walletSelectors.isPaymentUsingWallet({
          wallet,
          payment,
        } as RootState),
      });
    }
    // Note: DEPOSIT_ACCEPTED and DEPOSIT_DECLINED don't track payments since they're deposit-related
  }, []);

  useEffect(() => {
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'viewSale',
      action: 'render',
      activityInfo: {
        screen: pageVariant,
      },
    });
  }, []);

  // Handle accepted status for estimate pending
  if (
    pageVariant === PageVariant.ESTIMATE_PENDING &&
    saleSelectors.estimateStatus(sale) === SALE_MAP.STATUS.ACCEPTED
  ) {
    return acceptedSuccessScreen;
  }

  // Handle successful payment
  if (payment?.paymentStatus === TXN_MAP.STATUS.SUCCESS) {
    // For subscriptions, use the SuccessScreen with subscription props
    if (pageVariant === PageVariant.SUBSCRIPTION) {
      return (
        <Suspense fallback={<SpinnerCentered />}>
          <LazySuccessScreen />
        </Suspense>
      );
    }

    // For payment requests, use the generic SuccessScreen
    if (pageVariant === PageVariant.PAYMENT_REQUEST) {
      return (
        <Suspense fallback={<SpinnerCentered />}>
          <LazySuccessScreen />
        </Suspense>
      );
    }

    // For deposits, use the DepositSuccessScreen
    return (
      <Suspense fallback={<SpinnerCentered />}>
        <LazyDepositSuccessScreen
          state={{
            sale,
            companyInfo,
            payment,
            invoiceDocument,
            insight,
            config,
            wallet,
            featureFlags,
            auth,
            error,
            i18n,
            ixp,
            modal,
            postInvoiceSurvey,
            toast,
            chat,
          }}
          subTitle={
            <FormattedMessage
              id="ACCEPTED_SUCCESS_SCREEN_DEPOSIT_MESSAGE"
              defaultMessage="Deposit paid"
            />
          }
          viewEstimateHandler={viewPDF}
          downloadEstimate={handleDownloadPDF}
          downloadReceipt={(e) => {
            e.preventDefault?.();
            dispatch(saveReceiptAsPdf({ intl }));
          }}
        />
      </Suspense>
    );
  }

  return (
    <Fragment>
      <div className="main-wrapper">
        <SidePanel />

        <div className="main flex-coumn">
          {renderErrorIfAny()}
          <Card>
            {/* Render components based on page variant */}
            {mainCard()}

            {pageVariant !== PageVariant.DEPOSIT_PENDING && <Separator height={20} />}
          </Card>
          <div className="mobile-contact-info-container">
            {/* render faas widget for estimate/deposit accepted, declined, expired or pending */}
            {saleSelectors.typeSelector(sale) === TXN_MAP.TYPES.ESTIMATE && (
              <FaasWidget
                insight={insight}
                shouldShowFaasWidget={insightSelectors.isValidFinancingOfferSelector(insight)}
              />
            )}

            {/* Don't render contact info and footer for payment requests */}
            {pageVariant !== PageVariant.PAYMENT_REQUEST &&
              pageVariant !== PageVariant.SUBSCRIPTION && (
                <>
                  <ContactInfoActionCard
                    companyName={companyInfoSelectors.nameSelector(companyInfo) || ''}
                    companyAddress={companyInfoSelectors.addressSelector(companyInfo) || {}}
                    companyEmail={companyInfoSelectors.emailSelector(companyInfo) || ''}
                    companyPhone={companyInfoSelectors.phoneSelector(companyInfo) || ''}
                    companyWebAddr={companyInfoSelectors.webAddrSelector(companyInfo) || ''}
                    phoneNumber={saleSelectors.referenceNumberSelector(sale) || ''}
                  />
                </>
              )}
          </div>
          {pageVariant === PageVariant.PAYMENT_REQUEST &&
            saleSelectors.isOneToManyPaymentRequest(sale) && (
              <div className="paylink-merchant-info-card">
                <OneToManyMerchantInfoCard
                  companyAddress={companyInfoSelectors.addressSelector(companyInfo) || {}}
                  companyEmail={companyInfoSelectors.emailSelector(companyInfo) || ''}
                  companyPhone={companyInfoSelectors.phoneSelector(companyInfo) || ''}
                  companyWebAddr={companyInfoSelectors.webAddrSelector(companyInfo) || ''}
                  number={companyInfoSelectors.nameSelector(companyInfo) || ''}
                  companyName={companyInfoSelectors.nameSelector(companyInfo) || ''}
                  showModal={({ component, componentProps }) => {
                    dispatch(modalActions.show({ component, ...componentProps }));
                  }}
                />
              </div>
            )}
          {pageVariant === PageVariant.PAYMENT_REQUEST &&
            companyInfoSelectors.countrySelector(companyInfo as CompanyInfo) === 'us' && (
              <span className="feedback-w">
                <FeedbackLink transactionType="PAYMENT_REQUEST" />
              </span>
            )}
        </div>
      </div>

      {invoiceDocument.fetchPDFStatus === TXN_MAP.STATUS.SUCCESS && pdfStatus.url && (
        <iframe id="hiddenPDFIframe" src={pdfStatus.url} style={{ display: 'none' }} />
      )}

      <style jsx>{`
        .main-wrapper {
          display: flex;
          flex-grow: 1;
          flex-direction: row-reverse;
          margin: 0 auto;
          max-width: 1040px;
          width: 100%;

          .main {
            width: 65%;
            padding: 15px 0;
            word-break: break-word;
            @media screen and (max-width: ${breakpoints.md}) {
              padding: ${companyLogoUrl ? '43px' : '10px'} 16px 0;
              margin-bottom: ${paymentMethodType ? '160px' : '0'};
              padding-bottom: ${pageVariant === PageVariant.SUBSCRIPTION ? '200px' : '0'};
              max-width: 100%;
              width: 100%;
            }
            .mobile-contact-info-container {
              margin: 16px 0 0;
              display: none;
              justify-content: space-between;
              flex-direction: column;
              @media screen and (max-width: ${breakpoints.md}) {
                display: flex;
                flex-grow: 1;
              }
            }

            .paylink-merchant-info-card {
              display: none;

              @media screen and (max-width: ${breakpoints.md}) {
                display: block;
              }
            }

            .feedback-w {
              @media not screen and (max-width: ${breakpoints.md}) {
                display: none;
              }
            }
          }
        }

        .side-panel {
          width: 35%;
          padding-left: 20px;

          @media screen and (max-width: ${breakpoints.md}) {
            display: none;
          }
        }
      `}</style>
    </Fragment>
  );
};

// For Next.js compatibility - define as a property instead of static method
(undefinedPage as any).getInitialProps = async () => {
  return {
    pageProps: {
      hideFeedBackLink: true,
    },
  };
};

// Map Redux state to props - return the whole store
function mapStateToProps(store: RootState): RootState {
  return store;
}

// Connect component to Redux
const with_connectToRedux = connect(mapStateToProps)(undefinedPage);
const with_CPStore = CPStore(with_connectToRedux);

export default with_CPStore;
